/**
 * CONSENT-DEVICE-1 — THE LEDGER, and the one rule that outranks convenience.
 *
 * Contract (CONSENT-LEDGER-1, Track B, in flight):
 *   POST /api/consents/events  { consent_type, action, doc_id, version, sha256, locale, surface,
 *                                device_id, scope: "device" }
 *   GET  /api/consents/me/status?device_id=  ->  { camera_microphone: { state, version, expires_at } }
 *
 * UNTIL TRACK B MERGES this talks to a mocked adapter with the SAME contract, so the call sites and
 * their tests are the ones that ship. What the mock must never do is grant a skip: the goal says "no
 * local-only shortcut in production", and the shortcut is the whole risk here. A client that decides
 * for itself that consent is remembered has invented consent.
 *
 * SO EVERY FAILURE DIRECTION IS "ASK". Status unreachable, malformed, unhashable document, missing
 * endpoint, flag off — all of them resolve to showing the consent screen. Being asked twice is a
 * friction; being skipped wrongly is a teacher filmed under a consent she never gave for this
 * wording. Those are not symmetric and the code must not treat them as if they were.
 */
import { CONSENT_TYPE } from "@/features/consent/consentDoc";

export const CONSENT_ACTION = Object.freeze({
  ACCEPT: "accept",
  DECLINE: "decline",
  WITHDRAW: "withdraw",
});

export const CONSENT_SCOPE_DEVICE = "device";

/**
 * C-384 — the scope an UNTICKED consent is written at.
 *
 * ⚑ It is a scope change, not a skipped write. The audit invariant (C-48 item 4) is that a recording
 * never exists without its consent row, so an untick still logs: she consented to these words, and
 * granted nothing about this device. A `user`-scope row says exactly that.
 */
export const CONSENT_SCOPE_USER = "user";

/**
 * The transport. Injected in tests; in the app it is the mocked adapter until Track B's endpoints
 * exist, and then the real client with no change at any call site.
 */
let transport = null;

export function setConsentTransport(next) {
  transport = next || null;
}

/**
 * The mock. It RECORDS events so a test (and a developer) can see what would have been written, and
 * it reports NO remembered consent — because a mock that answered "remembered" would hand itself the
 * exact shortcut this module exists to refuse.
 */
export function createMockConsentTransport() {
  const events = [];
  return {
    events,
    async postEvent(payload) {
      events.push(payload);
      // `recorded: true` because this mock STANDS IN FOR A SERVER THAT WROTE THE EVENT, and under
      // the strict rule above `false` would make local development unable to record any consent at
      // all. It is honest for a stand-in and dangerous in production, which is why the install in
      // App.js and that strict rule had to land in the same commit: the guard that keeps this
      // unreachable from shipped code is
      // backend/tests/test_consent_ledger_1_transport_install.py, which asserts a non-test
      // installer exists and that its argument is not this mock.
      return { ok: true, recorded: true, mock: true };
    },
    async getStatus() {
      return { [CONSENT_TYPE]: { state: "none", version: null, expires_at: null } };
    },
  };
}

function activeTransport() {
  if (!transport) transport = createMockConsentTransport();
  return transport;
}

/**
 * Record one consent action. Resolves to true only when the ledger accepted it.
 *
 * The caller must await this BEFORE releasing anything that opens a camera. It returns a boolean
 * rather than throwing so a call site cannot accidentally proceed through a rejected promise.
 */
export async function recordConsentEvent({
  action,
  doc,
  deviceId,
  surface,
  remember = true,
}) {
  if (!action || !doc || !deviceId) return false;
  try {
    // ⚑ C-384 — AN UNTICK WRITES NO GRANT, and the scope is where that happens.
    //
    // This used to post `scope: "device"` unconditionally, so unticking "remember this device"
    // still wrote a device grant and the next recording was still skipped. The checkbox had no
    // consumer at all; `RecordPage` and `rememberDevice` both described the opposite.
    //
    // The write is the right place for it, not the skip: the skip asks the SERVER, so with no
    // device grant on the ledger the server's own answer is "not remembered" and she is asked
    // again. Fixing only the skip would have left a grant she declined sitting in the audit trail.
    //
    // ⚑ `device_id` IS OMITTED, NOT NULLED, on a user-scope row. The server refuses a non-device
    // row that carries one -- its own words: it "would make 'which devices has she consented on'
    // answerable two contradictory ways" -- so the key must be absent rather than present-and-null.
    // `remember` defaults TRUE so the withdraw path and every other caller are unchanged.
    const scope = remember ? CONSENT_SCOPE_DEVICE : CONSENT_SCOPE_USER;
    const res = await activeTransport().postEvent({
      consent_type: doc.consent_type || CONSENT_TYPE,
      action,
      doc_id: doc.doc_id,
      version: doc.version,
      sha256: doc.sha256,
      locale: doc.locale,
      surface: surface || "record",
      ...(remember ? { device_id: deviceId } : {}),
      scope,
      // First-sight registration (C-31 item 4). The server keys on (doc_id, locale, sha256).
      text: doc.text,
      source: doc.source,
    });
    // C-70 item 5, RULED: `recorded === true`, STRICTLY. A 200 that did not write is not a
    // consent — the server sets this field only after the insert returned, so it is the only field
    // that means "it is in the ledger".
    //
    // NOT `!== false`, which the first draft of the handback suggested so that a future server
    // omitting the field would not fail every camera. That reasoning inverts the asymmetry the
    // whole goal rests on: an omitted field would read as RECORDED and open the camera, which is
    // failing OPEN on a privacy fact. Being asked twice is friction; being filmed under a consent
    // nobody wrote is not. A missing field is a DEFECT and refuses.
    //
    // `ok` is no longer consulted: `recorded === true` already implies the request succeeded, and
    // keeping it in front would only add a second way to be wrong.
    return res?.recorded === true;
  } catch {
    return false;
  }
}

/**
 * May this teacher's consent screen be skipped on this device, for THIS wording?
 *
 * Every argument has to agree. In particular the status's version must equal the hash of the text
 * we are about to show: a remembered "granted" for different words is not consent to these.
 *
 * @returns {Promise<boolean>} true ONLY on an explicit, unexpired, same-version grant
 */
export async function isConsentRemembered({ doc, deviceId }) {
  if (!doc?.version || !deviceId) return false; // unhashable or no device: ask
  try {
    const status = await activeTransport().getStatus(deviceId);
    const entry = status?.[doc.consent_type || CONSENT_TYPE];
    if (!entry) return false;
    if (entry.state !== "granted") return false;
    // STRICT equality on the wording. A newer or older version means she has not seen these words.
    if (entry.version !== doc.version) return false;
    if (entry.expires_at) {
      const expires = Date.parse(entry.expires_at);
      if (Number.isNaN(expires) || expires <= Date.now()) return false;
    }
    return true;
  } catch {
    return false; // unreachable: ask
  }
}

export default recordConsentEvent;
